import { getDay, parse } from 'date-fns';

export type BulkMode = 'replace' | 'add';

// 4c の便はプルダウンで選ぶ登録パターン（shift_pattern_id）のみ。
// 一回限りのカスタム時刻は 4d（セル編集）で扱うため 4c の型・経路には含めない。
export type BulkApplyRequestBody = {
  truck_ids: number[];
  dates: string[];
  mode: BulkMode;
  shift_pattern_id: number;
  overwrite_mode?: 'whole' | 'split';
};

export type GridSelection = {
  isTruckSelected: (truckId: number) => boolean;
  isDateSelected: (date: string) => boolean;
  toggleTruck: (truckId: number) => void;
  setTrucksSelected: (truckIds: number[], selected: boolean) => void;
  toggleDate: (date: string) => void;
};

const dowOf = (date: string): number => getDay(parse(date, 'yyyy-MM-dd', new Date()));

export const weekdayDatesInColumns = (dateColumns: string[], dow: number): string[] =>
  dateColumns.filter((date) => dowOf(date) === dow);

export const weekdayOnlyDates = (dateColumns: string[]): string[] =>
  dateColumns.filter((date) => dowOf(date) !== 0 && dowOf(date) !== 6);

export const toggleSetValue = <T>(set: Set<T>, value: T): Set<T> => {
  const next = new Set(set);
  if (next.has(value)) next.delete(value); else next.add(value);
  return next;
};

// allowed に含まれる要素だけ残す（変化が無ければ同一参照を返す＝不要な再レンダー回避）。
// 期間移動で消えた日付・フィルタ/再取得で消えたトラックの選択を取り除くのに使う。
export const intersectSet = <T>(set: Set<T>, allowed: T[]): Set<T> => {
  const allowedSet = new Set(allowed);
  let changed = false;
  const next = new Set<T>();
  set.forEach((value) => { if (allowedSet.has(value)) next.add(value); else changed = true; });
  return changed ? next : set;
};

export const canApply = (truckCount: number, dateCount: number, patternId: number | null): boolean =>
  truckCount > 0 && dateCount > 0 && patternId !== null;

// 4c の一括反映は「上書き(replace)」固定。未配車勤務を削除して反映し、配車済み・固定済みは API 側で保護される。
// API は add も受けるが 4c では送らない（追加運用が必要になればここで mode を引数化する）。
export const buildBulkRequest = (
  truckIds: Set<number>,
  dates: Set<string>,
  patternId: number,
): BulkApplyRequestBody => ({
  truck_ids: Array.from(truckIds).sort((a, b) => a - b),
  dates: Array.from(dates).sort(),
  mode: 'replace',
  shift_pattern_id: patternId,
});

export type BulkConflict = {
  shiftCount: number;
  partialMultiDayCount: number;
  splitAvailable: boolean;
  wholeAvailable: boolean;
  blockedCount: number;
};

// 衝突サマリ（snake_case）を BulkConflict（camelCase）へ写像する。欠損は安全な既定（0 / false）。
const mapConflictSummary = (c: Record<string, unknown>): BulkConflict => ({
  shiftCount: Number(c.shift_count ?? 0),
  partialMultiDayCount: Number(c.partial_multi_day_count ?? 0),
  splitAvailable: c.split_available === true,
  wholeAvailable: c.whole_available === true,
  blockedCount: Number(c.blocked_count ?? 0),
});

// API の 409 conflict_required を判定して上書き確認に必要な件数を取り出す（実 create の後方互換フォールバック）。
// 409 以外（422 などの通常エラー）は null を返し、確認ダイアログを出さない。
export const parseConflictResponse = (status?: number, data?: unknown): BulkConflict | null => {
  if (status !== 409) return null;
  const errors = (data as { errors?: unknown })?.errors;
  if (!Array.isArray(errors) || !errors.includes('conflict_required')) return null;
  return mapConflictSummary((data as { conflict?: Record<string, unknown> })?.conflict ?? {});
};

// 200 チェックレスポンス { conflict: {...}|null } を解釈。conflict が無ければ null（衝突なし）。
// 反映/解除前の事前チェックでこれを使い、実処理を 409 で判定する経路を廃す。
export const parseCheckConflict = (data?: unknown): BulkConflict | null => {
  const c = (data as { conflict?: Record<string, unknown> | null })?.conflict;
  if (!c) return null;
  return mapConflictSummary(c);
};

export type BulkRemoveRequestBody = { truck_ids: number[]; dates: string[]; overwrite_mode?: 'whole' | 'split' };

export const buildRemoveRequest = (truckIds: Set<number>, dates: Set<string>): BulkRemoveRequestBody => ({
  truck_ids: Array.from(truckIds).sort((a, b) => a - b),
  dates: Array.from(dates).sort(),
});

// 上書き確認 OK 後の再送 body。初回と同じ内容に overwrite_mode を足す。
// ジェネリック化により BulkApplyRequestBody / BulkRemoveRequestBody 両方で使える。
export const buildOverwriteRetryBody = <T extends { overwrite_mode?: 'whole' | 'split' }>(body: T, mode: 'whole' | 'split'): T => ({
  ...body,
  overwrite_mode: mode,
});
